/**
 * AssignUserModal Component
 *
 * Модальное окно для назначения сейла или менеджера на организацию.
 * Логика отличается в зависимости от роли текущего пользователя.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Select,
  Button,
  Group,
  Text,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import {
  IconCheck,
  IconUserPlus,
  IconAlertCircle,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Модальное окно для назначения пользователя
 */
const AssignUserModal = ({
  opened,
  onClose,
  type, // 'sales' | 'manager'
  organizationId,
  currentUser,
  onSuccess,
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [error, setError] = useState(null);

  const isSalesRole = currentUser?.role === 'SALES';
  const title = type === 'sales' ? 'Назначить сейла' : 'Назначить менеджера';

  // Загрузка списка пользователей
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        // Для сейлов загружаем всех sales-managers, для менеджеров - только managers
        const endpoint = type === 'sales'
          ? '/api/users/sales-managers'
          : '/api/users/by-role/managers';

        const response = await fetch(endpoint, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Не удалось загрузить список пользователей');
        }

        const data = await response.json();
        setUsers(data.items || []);
      } catch (err) {
        console.error('Failed to load users:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (opened && !isSalesRole) {
      fetchUsers();
    }
  }, [opened, type, isSalesRole]);

  // Устанавливаем текущего пользователя по умолчанию
  useEffect(() => {
    if (opened && currentUser && !isSalesRole) {
      setSelectedUserId(currentUser.id);
    }
  }, [opened, currentUser, isSalesRole]);

  const handleSubmit = async () => {
    const userId = isSalesRole ? currentUser.id : selectedUserId;

    if (!userId) {
      setError('Выберите пользователя');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const endpoint = type === 'sales'
        ? `/api/organizations/${organizationId}/assign-sales`
        : `/api/organizations/${organizationId}/assign-manager`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при назначении');
      }

      const updatedOrg = await response.json();

      if (onSuccess) {
        onSuccess(updatedOrg);
      }

      onClose();
    } catch (err) {
      console.error('Failed to assign user:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Для SALES роли - простое подтверждение
  if (isSalesRole && type === 'sales') {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title="Взять организацию"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Вы хотите взять эту организацию себе?
          </Text>

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
            >
              {error}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}
              loading={submitting}
            >
              Подтвердить
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  // Для остальных ролей - выбор пользователя
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="md"
    >
      <Stack gap="md">
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : (
          <>
            <Select
              label={type === 'sales' ? 'Выберите сейла' : 'Выберите менеджера'}
              placeholder="Начните вводить имя..."
              data={users.map(u => ({
                value: String(u.id),
                label: u.full_name || u.email,
              }))}
              value={selectedUserId ? String(selectedUserId) : null}
              onChange={(value) => setSelectedUserId(value ? parseInt(value) : null)}
              searchable
              clearable
              nothingFoundMessage="Пользователи не найдены"
            />

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
              >
                {error}
              </Alert>
            )}

            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={onClose}
                disabled={submitting}
              >
                Отмена
              </Button>
              <Button
                color="blue"
                leftSection={<IconUserPlus size={16} />}
                onClick={handleSubmit}
                loading={submitting}
                disabled={!selectedUserId}
              >
                Назначить
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

/**
 * Модальное окно успешного назначения (для SALES)
 */
export const AssignSuccessModal = ({ opened, onClose }) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Успешно"
      centered
      size="sm"
    >
      <Stack gap="md" align="center" py="md">
        <IconCheck
          size={48}
          color={tokens.colors.semantic.success.primary}
        />
        <Text size="lg" fw={500} ta="center">
          Организация успешно назначена вам
        </Text>
        <Button onClick={onClose} color="green">
          Закрыть
        </Button>
      </Stack>
    </Modal>
  );
};

export default AssignUserModal;
